import insigneAnime from '@cibli/shared/assets/insigne-vert-noir-anime.svg';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'react-router';

import { cn } from '@/lib/utils';

import CancelModal from './CancelModal';
import EndModal from './EndModal';
import IconButton from './IconButton';

// eslint-disable-next-line react-refresh/only-export-components -- enum is coupled to Header props
export enum CloseTypeEnum {
  End = 'end',
  Cancel = 'cancel',
  Muted = 'muted',
}

/**
 * Sticky header band height — single source of truth.
 * MainLayout reserves the same space for the page content (CONTENT_MIN_HEIGHT_CLASS),
 * so screens never need their own top-offset guess (pt-[90px], mt-[90px], ...).
 */
export const HEADER_HEIGHT_CLASS = 'min-h-[72px] sm:min-h-[96px]';

/** Viewport height minus the sticky header, so screens still fill exactly one screen. */
export const CONTENT_MIN_HEIGHT_CLASS =
  'min-h-[calc(100dvh_-_72px)] sm:min-h-[calc(100dvh_-_96px)]';

type HeaderProps = {
  onClose?: () => void;
  middleContent?: React.ReactNode;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  closeType?: CloseTypeEnum;
  className?: string;
  logoContainerClassName?: string;
};

const Header = ({
  onClose,
  middleContent,
  leftContent,
  rightContent,
  closeType = CloseTypeEnum.Cancel,
  className,
  logoContainerClassName,
}: HeaderProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { pathname } = useLocation();

  // Interview session: from /interview until the end of the session (reset), the header
  // shows the animated insigne instead of the full Cibli Job lockup.
  const isInterviewSession = pathname === '/interview' || pathname.startsWith('/interview/');
  const logoSrc = isInterviewSession ? insigneAnime : '/logo-ciblijob-anime.svg';

  const handleClose = () => {
    if (closeType === CloseTypeEnum.Muted) {
      onClose?.();
    } else {
      setModalOpen(true);
    }
  };

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 flex flex-wrap items-center gap-3 px-4 sm:px-8',
          'border-b border-black/5 bg-background/95 backdrop-blur-sm',
          HEADER_HEIGHT_CLASS,
          className,
        )}
      >
        <div className={cn('flex min-w-0 items-center gap-2', logoContainerClassName)}>
          <img
            src={logoSrc}
            alt="Cibli Job"
            className="h-[56px] w-auto max-w-[42vw] object-contain md:h-[72px]"
          />
          {leftContent}
        </div>
        {middleContent && <div className="flex min-w-0 flex-1 justify-center">{middleContent}</div>}
        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
          {rightContent}
          {onClose && (
            <IconButton
              icon={<X strokeWidth={3} className="min-h-6 min-w-6" />}
              onClick={handleClose}
            />
          )}
        </div>
      </header>
      {onClose &&
        (closeType === CloseTypeEnum.Cancel ? (
          <CancelModal open={modalOpen} onOpenChange={setModalOpen} onConfirm={onClose} />
        ) : closeType === CloseTypeEnum.End ? (
          <EndModal open={modalOpen} onOpenChange={setModalOpen} onConfirm={onClose} />
        ) : null)}
    </>
  );
};

export default Header;
